import {Flexidate, makeDayTstzSpan, spawnTstzSpan} from "../tstzspan.ts";
import {
    AgentStamp,
    AgentStub,
    ScheduledTransfer,
    SortOrder,
    TruckStub,
    TruckTenure,
    useAgentById,
    useStampsForAgent,
    useStampsForAgentDuring,
} from "../rpc/webrpc-client.gen.ts";
import {StampSceneView} from "../stamp/stamps.tsx";
import {BaseTimelineScene, cmpScenesNewestFirst} from "../timeline/timeline.tsx";
import {z} from "zod/v4";
import {ScheduledTransferScene, synthScheduleScene} from "../transit/schedule.tsx";
import {SuspenseDialog} from "../components/SuspenseDialog.tsx";
import {DateArg} from "date-fns";
import {StampFlavorIconMap} from "../stamp/stampFlavorIconMap.tsx";
import {DiaryView} from "../timeline/diary.tsx";
import {useMemo} from "react";
import {TimelineTimePoint} from "../timeline/timelineTimeCoordinate.tsx";

export const Timeslice = z.object({
    day: Flexidate,
});
export type Timeslice = z.infer<typeof Timeslice>;

const AgentSceneBase = z.object({
    ...BaseTimelineScene.shape,
    subjectFlavor: z.literal("agent"),
    subject: AgentStub,
});

export const AgentStampScene = z.object({
    ...AgentSceneBase.shape,
    sceneFlavor: z.literal("stamp"),
    stamp: AgentStamp,
});
export type AgentStampScene = z.infer<typeof AgentStampScene>;

export const DriverSceneBase = z.object({
    ...AgentSceneBase.shape,
    role: z.literal("driver"),
});

export const DriverTenureScene = z.object({
    ...DriverSceneBase.shape,
    sceneFlavor: z.literal("crew"),
    truck: TruckStub,
    direction: z.literal(["mount", "dismount"]),
    when: TimelineTimePoint,
});
export type DriverTenureScene = z.infer<typeof DriverTenureScene>;

export const DriverScheduleScene = z.object({
    ...DriverSceneBase.shape,
    sceneFlavor: z.literal("schedule"),
    scheduledTransferScene: ScheduledTransferScene,
});
export type DriverScheduleScene = z.infer<typeof DriverScheduleScene>;

export const DriverScene
    = z.discriminatedUnion("sceneFlavor",
    [AgentStampScene, DriverTenureScene, DriverScheduleScene]);
export type DriverScene = z.infer<typeof DriverScene>;

export interface AgentTimelineViewProps {
    who: string;
    day?: DateArg<Date>;
    sortOrder?: SortOrder;
}

// interface AgentTimeslice {
//     who: string;
//     // leave span out if you want reactivity and push
//     span?: TstzSpan;
// }

export const iconsForAgentStampFlavor: StampFlavorIconMap<"agent"> = {} as const;

function synthStampScenes({agent, stamps}: { agent: AgentStub, stamps: AgentStamp[] }): AgentStampScene[] {
    return stamps.map(stamp => ({
        subjectFlavor: "agent",
        subject: agent,
        sceneFlavor: "stamp",
        stamp,
        when: {at: stamp.posted},
    }));
}

export function synthDriverTruckTenureScenes({driver, tenures: tenures}: {
    driver: AgentStub,
    tenures: TruckTenure[]
}): DriverTenureScene[] {
    const rv: DriverTenureScene[] = [];

    for (const tenure of tenures) {
        const validSpan = spawnTstzSpan(tenure.validSpan);
        if (validSpan.lower) {
            rv.push({
                subjectFlavor: "agent",
                subject: driver,
                role: "driver",
                sceneFlavor: "crew",
                when: {at: validSpan.lower},
                direction: "mount",
                truck: tenure.truck,
            });
        }
        if (validSpan.upper) {
            rv.push({
                subjectFlavor: "agent",
                subject: driver,
                role: "driver",
                sceneFlavor: "crew",
                when: {at: validSpan.upper},
                direction: "dismount",
                truck: tenure.truck,
            });
        }
    }

    return rv;
}

export function synthDriverScheduleScenes({driver, scheduledTransfers}: {
    driver: AgentStub,
    scheduledTransfers: ScheduledTransfer[]
}): DriverScheduleScene[] {
    const rv: DriverScheduleScene[] = scheduledTransfers.map(sxfer => {
        const sxScene = synthScheduleScene(sxfer);
        return {
            subjectFlavor: "agent",
            subject: driver,
            role: "driver",
            when: sxScene.when,
            where: sxScene.where,
            asof: sxScene.asof,
            sceneFlavor: "schedule",
            scheduledTransferScene: sxScene,
        };
    });
    return rv;
}


//
// function useAgentStampTimelineScenes({who, span}: AgentTimeslice): DiaryEntryProps[] {
//     const {t} = useTranslation();k
//     const theme = useTheme();
//     const mama = useMothership();
//     const tqo = span && !span.contains(new Date())
//         ? agentStampsDuringTqxo(mama, {agentId: who, span})
//         : watchStampsForAgentTqo(mama, {agentId: who});
//     const {data: stampsData, isSuccess: isStampsSuccess}
//         = useRefreshableQuery(tqo);
//     const stamps = isStampsSuccess ? stampsData.stamps : [];
//
//     const rv: DiaryEntryProps[] = [];
//     let lastBreakStart: AgentStamp | null = null;
//     for (const stamp of stamps) {
//         if (!stamp.flavor) {
//             rv.push({
//                 when: {at: stamp.posted},
//                 what: <StampTimelineEntry stamp={stamp} color="neutral" variant="soft"/>,
//                 color: "neutral",
//                 variant: "soft",
//             });
//         }
//         switch (stamp.flavor) {
//             case AgentStampFlavor.SHIFT_START:
//             case AgentStampFlavor.SHIFT_END: {
//                 const paletteName = "neutral";
//                 const variant = "soft";
//                 const label = stamp.flavor === AgentStampFlavor.SHIFT_END ? t("diary.endedShift") : t("diary.beganShift");
//                 rv.push({
//                     when: {at: stamp.posted},
//                     what: <StampTimelineEntry
//                         stamp={stamp}
//                         extraChildren={<TimelineCell>
//                             <Typography level="title-md"
//                                         sx={{
//                                             color: "inherit",
//                                             bgcolor: "inherit",
//                                             py: 1.2,
//                                         }}
//                             >{label}</Typography></TimelineCell>
//                         }
//                         color={paletteName}
//                         variant={variant}/>
//                 });
//             }
//                 break;
//             case AgentStampFlavor.BREAK_END:
//                 if (lastBreakStart) {
//                     const span = new TstzSpan({
//                         lower: lastBreakStart.posted,
//                         upper: stamp.posted,
//                     });
//                     if (isValidTstzSpan(span)) {
//                         const paletteName = "warning";
//                         const variant = "soft";
//                         rv.push({
//                             when: {span},
//                             what: <StampTimelineEntry
//                                 stamp={stamp}
//                                 extraChildren={
//                                     <TimelineCell>
//                                         <Box sx={{
//                                             color: "inherit",
//                                             bgcolor: "inherit",
//                                             fontSize: theme.fontSize.xl,
//                                             p: 1.2,
//                                         }}
//                                         >{t("diary.onBreak")}</Box></TimelineCell>
//                                 }
//                                 color={paletteName}
//                                 variant={variant}/>,
//                             color: paletteName,
//                             variant,
//                         });
//                         lastBreakStart = null;
//                     }
//                 }
//                 break;
//             case
//             AgentStampFlavor.BREAK_START:
//                 lastBreakStart = stamp;
//                 break;
//         }
//     }
//
//     if (lastBreakStart) {
//         const paletteName = "warning";
//         const variant = "soft";
//         rv.push({
//             when: {at: lastBreakStart.posted},
//             what: <StampTimelineEntry
//                 stamp={lastBreakStart}
//                 extraChildren={
//                     <TimelineCell>
//                         <Box sx={{
//                             color: "inherit",
//                             bgcolor: "inherit",
//                             fontSize: theme.fontSize.xl,
//                             p: 1.2,
//                         }}
//                         >{t("diary.beginBreak")}</Box></TimelineCell>
//                 }
//                 color={paletteName}
//                 variant={variant}/>,
//             color: paletteName,
//             variant,
//         });
//     }
//
//     return rv;
// }
//
// function useAgentCrewTenureTimelineScenes({who, span: inSpan}: AgentTimeslice): DiaryEntryProps[] {
//     const span = inSpan ?? todaySpan();
//     const mama = useMothership();
//     const {data, isSuccess}
//         = useRefreshableQuery(truckCrewTenuresByAgentDuringTqxo(mama, {agentId: who, span}));
//     const {t} = useTranslation();
//     const tenures = isSuccess ? data.tenures : [];
//     const rv: DiaryEntryProps[] = tenures.reduce<DiaryEntryProps[]>((acc, tenure) => {
//         const tenureSpan = new TstzSpan(tenure.validSpan);
//         if (span.contains(tenureSpan.lower)) {
//             acc.push({
//                 when: {at: tenureSpan.lower!},
//                 what: <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
//                     <TruckCapsule truck={tenure.truck}/>
//                     <Box>{t("driver.mountedTruck")}</Box>
//                 </Stack>,
//                 color: "neutral",
//                 variant: "soft",
//             });
//         }
//         if (span.contains(tenureSpan.upper)) {
//             acc.push({
//                 when: {at: tenureSpan.upper!},
//                 what: <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
//                     <TruckCapsule truck={tenure.truck}/>
//                     <Box>{t("driver.dismountedTruck")}</Box>
//                 </Stack>,
//                 color: "neutral",
//                 variant: "soft",
//             });
//         }
//         return acc;
//     }, []);
//     return rv;
// }
//
// // these are guaranteed to be sorted
// function useAgentTimelineSceneProps({who, span, sortOrder}: AgentTimeslice & {
//     sortOrder: SortOrder
// }): DiaryEntryProps[] {
//     const stampScenes = useAgentStampTimelineScenes({who, span});
//     const truckCrewingScenes = useAgentCrewTenureTimelineScenes({who, span});
//     const unsorted = useMemo(() => {
//         return [
//             ...stampScenes,
//             ...truckCrewingScenes,
//         ];
//     }, [stampScenes, truckCrewingScenes]);
//     const cmp = useTimeCoordinatedComparator(sortOrder);
//     const rv: DiaryEntryProps[] = useMemo(() => {
//         const sorted = unsorted.toSorted(cmp);
//         return sorted;
//     }, [cmp, unsorted]);
//     return rv;
// }

interface DriverTimelineFood {
    driver: AgentStub;
    stamps: AgentStamp[];
    scheduledTransfers: ScheduledTransfer[];
}

export function synthDriverScenes(food: DriverTimelineFood): DriverScene[] {
    const unsorted: DriverScene[] = [
        ...synthStampScenes({agent: food.driver, stamps: food.stamps}),
        ...synthDriverScheduleScenes(food),
    ];

    const sorted = unsorted.toSorted(cmpScenesNewestFirst);
    return sorted;
}

export function AgentTimelineView({who, day, sortOrder}: AgentTimelineViewProps) {
    const span = day ? makeDayTstzSpan(day) : undefined;
    const strspan = span?.asTimeIntervalString();
    const agentTqrv = useAgentById({agentId: who});
    const liveTqrv = useStampsForAgent({agentId: who});
    const eraTqrv = useStampsForAgentDuring(strspan ? {agentId: who, during: strspan} : undefined);
    const stamps = strspan ? eraTqrv.data : liveTqrv.data;
    const scenes = useMemo(() => {
        const descScenes =
            agentTqrv.data && stamps
                ? synthStampScenes({agent: agentTqrv.data, stamps})
                : [];
        const rv =
            sortOrder === SortOrder.enum.ASC
                ? descScenes.reverse()
                : descScenes;
        return rv;
    }, [agentTqrv.data, sortOrder, stamps]);

    if (!agentTqrv.data || !stamps) {
        return <SuspenseDialog message="fetching diary entries"/>
    }

    return <DiaryView sortedScenes={scenes}
                      Entry={props =>
                          <StampSceneView<"agent"> iconMap={iconsForAgentStampFlavor} {...props}/>}
    />
}

